'use client';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { eventSeriesScheduleSummary } from '@/utils/event-series-schedule';
import type { EventSeries } from '@/types/EventSeries';

type Props = {
  eventSeries: EventSeries;
};

const statusConfig: Record<string, { label: string; color: 'success' | 'warning' | 'error'; variant?: 'outlined' }> = {
  ACTIVE: { label: 'Active', color: 'success', variant: 'outlined' },
  PAUSED: { label: 'Paused', color: 'warning' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
};

const borderColors = {
  ACTIVE: undefined,
  PAUSED: 'warning.main',
  CANCELLED: 'error.main',
};

const statusMessages: Record<string, string> = {
  PAUSED: 'This series is currently paused',
  CANCELLED: 'This series has been cancelled',
};

const EventSeriesStatusScheduleBanner = ({ eventSeries }: Props) => {
  const status = eventSeries.status || 'ACTIVE';
  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.ACTIVE;
  const borderColor = borderColors[status as keyof typeof borderColors];
  const statusMessage = statusMessages[status];
  const scheduleSummary = eventSeriesScheduleSummary(eventSeries);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        ...(borderColor ? { borderColor } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip label={config.label} color={config.color} variant={config.variant} size="small" />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={status === 'CANCELLED' ? { textDecoration: 'line-through' } : undefined}
        >
          {scheduleSummary}
        </Typography>
      </Box>
      {statusMessage && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {statusMessage}
        </Typography>
      )}
    </Paper>
  );
};

export default EventSeriesStatusScheduleBanner;
